import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

// CB-16 theme context (copy of website/src/shell/theme-context.tsx, admin keys).
// ThemePref light | dark, always written as data-theme on <html>. Light is the
// default; only an explicit stored 'dark' switches — the OS colour scheme is never
// consulted. The pref lives in localStorage only (DC43 brief; the design's "REPLACE
// WITH PATCH /admin/me/preferences" has no contract operation). The pre-paint script
// in index.html reads the same key before first paint — keep the two in step.

export type ThemePref = 'light' | 'dark';

interface ThemeContextValue {
  pref: ThemePref;
  /** What is showing (always equal to pref). */
  resolved: 'light' | 'dark';
  setPref: (pref: ThemePref) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
export const THEME_STORAGE_KEY = 'bd.admin.theme';

export function readStoredPref(): ThemePref {
  try {
    const saved =
      typeof localStorage !== 'undefined' ? localStorage.getItem(THEME_STORAGE_KEY) : null;
    return saved === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readStoredPref);
  const resolved: 'light' | 'dark' = pref;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', pref);
    root.style.colorScheme = pref;
    // Browser chrome follows the canvas token (value lives in theme.css only).
    const meta = document.querySelector('meta[name="theme-color"]');
    const canvas = getComputedStyle(root).getPropertyValue('--canvas').trim();
    if (meta && canvas) meta.setAttribute('content', canvas);
  }, [pref]);

  const setPref = useCallback((next: ThemePref) => {
    setPrefState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Storage blocked — the choice still applies for this session.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ pref, resolved, setPref }),
    [pref, resolved, setPref],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
